import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Dialog, DialogContent, useMediaQuery } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { FormProvider, useForm } from "react-hook-form";
import moment from "moment";
import { HubConnectionState } from "@microsoft/signalr";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import Loader from "../../../shared/components/Loader";
import ActionToast from "../../../shared/components/ActionToast";
import { NotificationMessage } from "../../../shared/models/models";
import {
  Offer,
  OfferFormData,
  OffersState,
  CreateOffer,
  ChangeOffer,
  OfferFile,
  OfferServiceRequest,
  ValidityPeriodCode,
} from "../models/models";
import { PublicServiceRequest } from "../../serviceBoard/models/models";
import {
  getOffer,
  createOffer,
  changeOffer,
  submitOffer,
  clearOffer,
  clearError,
} from "../customerOffersSlice";
import publicServiceRequestsApi from "../../serviceBoard/api/publicServiceRequestsApi";
import publicCustomersApi from "../../../public/api/publicCustomersApi";
import WizardStepper from "./WizardStepper";
import WizardNavigation from "./WizardNavigation";
import StepPricing from "./steps/StepPricing";
import StepScheduling from "./steps/StepScheduling";
import StepServiceNotes from "./steps/StepServiceNotes";
import StepReviewSend from "./steps/StepReviewSend";
import OfferRfsSummary from "./shared/OfferRfsSummary";
import DeparturePointModal, { DepartureLocation } from "./pricing/DeparturePointModal";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import usePermission from "../../../shared/hooks/usePermission";
import { Modules } from "../../../core/models/PermissionModels";
import PricingDefaultsBanner from "./shared/PricingDefaultsBanner";
import useOfferPricingDefaults from "../hooks/useOfferPricingDefaults";
import useDirectionsCalculation, { LatLng } from "../hooks/useDirectionsCalculation";
import serviceProviderPricingApi, {
  ServiceProviderSettings,
  ProviderDeadlineInfo,
} from "../../settings/api/serviceProviderPricingApi";
import { getSharedConnection } from "../../../shared/notifications/signalrConnection";
import useDataRefresh from "../../../shared/notifications/hooks/useDataRefresh";
import RequestNoLongerAcceptingDialog from "./RequestNoLongerAcceptingDialog";
import DuplicateOfferDialog from "./DuplicateOfferDialog";
import RfsUnavailableBanner from "./RfsUnavailableBanner";
import offersApi from "../api/offersApi";

const dt = {
  pageBg: "#F0F2F7",
  pageDotColor: "rgba(52, 152, 219, 0.04)",
  textPrimary: "#1a1a2e",
  textSecondary: "#6B7280",
  blue: "var(--accent, #3498DB)",
  green: "#10B981",
  greenGlow: "rgba(16, 185, 129, 0.12)",
  font: 'var(--font-sans)',
  radius: "14px",
};

const STEP_COUNT = 4;

const EditOfferPage = () => {
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const navigate = useNavigate();
  const { id, serviceRequestId } = useParams();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery("(max-width:768px)");
  const { canWrite } = usePermission(Modules.Servicing);
  const isReadOnly = !canWrite;

  const { offer, loading, error } = useAppSelector<OffersState>(
    (state: any) => state.customerOffers
  );

  const [serviceRequest, setServiceRequest] = useState<OfferServiceRequest | undefined>(undefined);
  const [fullServiceRequest, setFullServiceRequest] = useState<PublicServiceRequest | undefined>(undefined);
  const [files, setFiles] = useState<OfferFile[]>([]);
  const [previewOffer, setPreviewOffer] = useState<Partial<Offer>>();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showClosedRfsDialog, setShowClosedRfsDialog] = useState(false);
  const [rfsUnavailable, setRfsUnavailable] = useState(false);

  const [existingOffer, setExistingOffer] = useState<any>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  const [deadlineInfo, setDeadlineInfo] = useState<ProviderDeadlineInfo | null>(null);

  // Provider settings (for location)
  const [providerSettings, setProviderSettings] = useState<ServiceProviderSettings | null>(null);
  // Customer location (destination)
  const [customerLocation, setCustomerLocation] = useState<LatLng | null>(null);
  // Departure location override
  const [departureLocation, setDepartureLocation] = useState<DepartureLocation | null>(null);
  const [showDepartureModal, setShowDepartureModal] = useState(false);

  const isNew = !id;
  const offerNumber = offer?.internalNumber || undefined;

  const stepLabels = useMemo(
    () => [
      translate("stepPricing"),
      translate("stepScheduling"),
      translate("stepServiceNotes"),
      translate("stepReviewSend"),
    ],
    [translate]
  );

  const emptyFormData = useMemo<OfferFormData>(
    () => ({
      laborCost: undefined,
      laborHours: undefined,
      hourlyRate: undefined,
      partsCost: undefined,
      materialDescription: "",
      travelCost: undefined,
      travelDistanceKm: undefined,
      travelRatePerKm: undefined,
      travelTimeHours: undefined,
      travelRatePerHour: undefined,
      additionalCosts: undefined,
      additionalCostsNote: "",
      laborSliderAdjustment: 0,
      materialSliderAdjustment: 0,
      travelDistanceSliderAdjustment: 0,
      travelTimeSliderAdjustment: 0,
      additionalSliderAdjustment: 0,
      earliestStartDate: undefined,
      immediateStart: false,
      availableDates: [],
      estimatedDuration: "",
      completionTimeCode: undefined,
      customCompletionTime: "",
      serviceApproach: "",
      specialEquipmentRequired: "",
      warrantyPeriodCode: undefined,
      customWarrantyPeriod: "",
      paymentTermsCode: undefined,
      customPaymentTerms: "",
      validityPeriodCode: ValidityPeriodCode.sevenDays,
      customValidityPeriod: "",
      messageToCustomer: "",
      includeDiagnostics: true,
      includeLaborForRepair: true,
      includePartsAndMaterials: false,
      includeTestingAndCalibration: false,
      includeWorkAreaCleanup: false,
      includeDetailedServiceReport: false,
      confirmedBindingOffer: false,
      confirmedTermsOfService: false,
      confirmedAuthorityToOffer: false,
      notifyOnViewAcceptReject: true,
      notifyTeamMembers: false,
    }),
    []
  );

  const formMethods = useForm<OfferFormData>({ defaultValues: emptyFormData });
  const { handleSubmit, reset, trigger, getValues, setValue } = formMethods;

  const {
    pricingLoading,
    fieldsFromDefaults,
    clearDefaultIndicator,
    hasDefaults,
  } = useOfferPricingDefaults(formMethods, isNew);

  // --- Provider location (origin for directions calc) ---
  const providerHasLocation = !!(
    providerSettings?.effectiveLatitude && providerSettings?.effectiveLongitude
  );

  // Origin: departure override -> provider effective location
  const originLocation = useMemo<LatLng | null>(() => {
    if (departureLocation) {
      return { lat: departureLocation.lat, lng: departureLocation.lng };
    }
    if (providerSettings?.effectiveLatitude && providerSettings?.effectiveLongitude) {
      return {
        lat: providerSettings.effectiveLatitude,
        lng: providerSettings.effectiveLongitude,
      };
    }
    return null;
  }, [departureLocation, providerSettings]);

  // Company address for "reset to company" in departure modal
  const companyAddress = useMemo<DepartureLocation | null>(() => {
    if (providerSettings?.effectiveLatitude && providerSettings?.effectiveLongitude) {
      return {
        lat: providerSettings.effectiveLatitude,
        lng: providerSettings.effectiveLongitude,
        label: translate("departure.companyDefault"),
      };
    }
    return null;
  }, [providerSettings, translate]);

  // --- Directions calculation ---
  const {
    distanceKm: calcDistanceKm,
    durationHours: calcDurationHours,
    loading: directionsLoading,
    isDirections,
  } = useDirectionsCalculation(originLocation, customerLocation);

  // Track when departure was explicitly changed by the user (via modal)
  const departureChangedRef = useRef(false);

  // Auto-fill travel distance/time when directions calculation completes
  const directionsApplied = useRef(false);
  useEffect(() => {
    // Skip while still loading
    if (directionsLoading) return;
    // Must have a result
    if (calcDistanceKm <= 0 && calcDurationHours <= 0) return;

    // Auto-fill when: (a) new offer initial load, or (b) user explicitly changed departure
    if (!isNew && !departureChangedRef.current) return;

    // Auto-fill distance km and travel time hours
    setValue("travelDistanceKm", calcDistanceKm);
    setValue("travelTimeHours", Math.round(calcDurationHours * 10) / 10);
    directionsApplied.current = true;
    departureChangedRef.current = false;
  }, [calcDistanceKm, calcDurationHours, directionsLoading, isNew, setValue, departureLocation]);

  // Determine travel info source
  const travelInfoSource = useMemo<"directions" | "estimated" | "manual" | null>(() => {
    if (directionsLoading) return null;
    if (!originLocation || !customerLocation) return "manual";
    if (isDirections) return "directions";
    if (calcDistanceKm > 0) return "estimated";
    return "manual";
  }, [directionsLoading, originLocation, customerLocation, isDirections, calcDistanceKm]);

  // --- toOfferBase: updated calculation ---
  const toOfferBase = useCallback((data: OfferFormData): Omit<ChangeOffer, "files"> => {
    // Labor: direct value (no slider adjustment)
    const laborFinal = data.laborCost ? Number(data.laborCost) : 0;

    // Material: direct value (no slider adjustment)
    const materialFinal = data.partsCost ? Number(data.partsCost) : 0;

    // Travel: percentage-based fine-tune: finalValue = baseValue * (1 + percentage / 100)
    const km = Number(data.travelDistanceKm) || 0;
    const ratePerKm = Number(data.travelRatePerKm) || 0;
    const travelDistanceBase = km * ratePerKm;
    const travelDistanceFinal = travelDistanceBase * (1 + (data.travelDistanceSliderAdjustment || 0) / 100);

    const hrs = Number(data.travelTimeHours) || 0;
    const ratePerHr = Number(data.travelRatePerHour) || 0;
    const travelTimeBase = hrs * ratePerHr;
    const travelTimeFinal = travelTimeBase * (1 + (data.travelTimeSliderAdjustment || 0) / 100);

    const travelFinal = Math.max(0, travelDistanceFinal) + Math.max(0, travelTimeFinal);

    // Additional: direct value (no slider adjustment)
    const additionalFinal = data.additionalCosts ? Number(data.additionalCosts) : 0;

    return {
      laborCost: laborFinal || undefined,
      laborHours: data.laborHours ? Number(data.laborHours) : undefined,
      hourlyRate: data.hourlyRate ? Number(data.hourlyRate) : undefined,
      partsCost: materialFinal || undefined,
      materialDescription: data.materialDescription || undefined,
      travelCost: travelFinal || undefined,
      travelDistanceKm: data.travelDistanceKm ? Number(data.travelDistanceKm) : undefined,
      travelRatePerKm: data.travelRatePerKm ? Number(data.travelRatePerKm) : undefined,
      travelTimeHours: data.travelTimeHours ? Number(data.travelTimeHours) : undefined,
      travelRatePerHour: data.travelRatePerHour ? Number(data.travelRatePerHour) : undefined,
      additionalCosts: additionalFinal || undefined,
      additionalCostsDescription: data.additionalCostsNote || undefined,
      earliestStartDate: data.immediateStart
        ? undefined
        : data.earliestStartDate
        ? moment(data.earliestStartDate).format("YYYY-MM-DD")
        : undefined,
      immediateStart: data.immediateStart || false,
      availableDates: data.availableDates && data.availableDates.length > 0
        ? JSON.stringify(data.availableDates)
        : undefined,
      estimatedDuration: data.estimatedDuration || undefined,
      completionTimeCode: data.completionTimeCode ? Number(data.completionTimeCode) : undefined,
      customCompletionTime: data.customCompletionTime || undefined,
      serviceApproach: data.serviceApproach || undefined,
      specialEquipmentRequired: data.specialEquipmentRequired || undefined,
      warrantyPeriodCode: data.warrantyPeriodCode ? Number(data.warrantyPeriodCode) : undefined,
      customWarrantyPeriod: data.customWarrantyPeriod || undefined,
      paymentTermsCode: data.paymentTermsCode ? Number(data.paymentTermsCode) : undefined,
      customPaymentTerms: data.customPaymentTerms || undefined,
      validityPeriodCode: data.validityPeriodCode ? Number(data.validityPeriodCode) : undefined,
      customValidityPeriod: data.customValidityPeriod || undefined,
      messageToCustomer: data.messageToCustomer || undefined,
      includeDiagnostics: data.includeDiagnostics || false,
      includeLaborForRepair: data.includeLaborForRepair || false,
      includePartsAndMaterials: data.includePartsAndMaterials || false,
      includeTestingAndCalibration: data.includeTestingAndCalibration || false,
      includeWorkAreaCleanup: data.includeWorkAreaCleanup || false,
      includeDetailedServiceReport: data.includeDetailedServiceReport || false,
      confirmedBindingOffer: true,
      confirmedTermsOfService: true,
      confirmedAuthorityToOffer: true,
      notifyOnViewAcceptReject: true,
      notifyTeamMembers: false,
    };
  }, []);

  const toPreviewOffer = (data: OfferFormData): Partial<Offer> => {
    return {
      ...toOfferBase(data),
      files,
    };
  };

  const onSubmit = async (data: OfferFormData, shouldSubmit: boolean = false) => {
    const model = toOfferBase(data);

    try {
      setIsSubmitting(true);

      if (isNew) {
        if (!serviceRequestId) return;

        const createModel: CreateOffer = {
          ...model,
          serviceRequestId,
          files,
        };

        const createResult = await dispatch(createOffer(createModel)).unwrap();

        if (shouldSubmit) {
          await dispatch(submitOffer(createResult.id)).unwrap();
        }
      } else {
        if (!id) return;

        const changeModel: ChangeOffer = {
          ...model,
          files,
        };

        await dispatch(changeOffer({ offerId: id, model: changeModel })).unwrap();

        if (shouldSubmit) {
          await dispatch(submitOffer(id)).unwrap();
        }
      }

      if (shouldSubmit) {
        setIsSubmitting(false);
        setShowSuccessDialog(true);
        return;
      }

      dispatch(clearOffer());
      navigate("/service-offers");
    } catch (err: any) {
      const errorCode = err?.code || "";
      const errorMessage = err?.message || err?.toString() || "";

      if (errorCode === "OFFER_SR_CLOSED" || errorMessage.includes("no longer accepting offers")) {
        setShowClosedRfsDialog(true);
      } else if (errorCode === "OFFER_DUPLICATE") {
        // Try to find the existing offer to show details
        if (serviceRequestId) {
          offersApi
            .getExistingOfferForSR(serviceRequestId)
            .then((found) => {
              if (found) {
                setExistingOffer({
                  id: found.id,
                  statusCode: found.statusCode,
                  laborCost: found.laborCost || 0,
                  totalEstimatedCost: found.totalEstimatedCost || 0,
                  createdAt: found.createdDate,
                  submittedAt: found.sentDate,
                  serviceApproach: found.serviceApproach,
                });
                setShowDuplicateDialog(true);
              } else {
                // Fallback: show the generic closed RFS dialog
                setShowClosedRfsDialog(true);
              }
            })
            .catch(() => setShowClosedRfsDialog(true));
        } else {
          setShowClosedRfsDialog(true);
        }
      } else if (errorCode === "OFFER_NOT_VISIBLE") {
        setShowClosedRfsDialog(true);
      } else if (errorCode === "OFFER_EXPIRED") {
        console.error("Offer expired:", err);
      } else {
        console.error("Failed to save offer:", err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    dispatch(clearOffer());
    navigate("/service-offers");
  };

  const handleNext = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    // Build preview data before going to review step
    if (activeStepIndex === STEP_COUNT - 2) {
      const data = getValues();
      setPreviewOffer(toPreviewOffer(data));
    }

    setActiveStepIndex((prev) => Math.min(prev + 1, STEP_COUNT - 1));
  };

  const handleBack = () => {
    setActiveStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSaveDraft = async () => {
    const data = getValues();
    await onSubmit(data, false);
  };

  const handleSubmitOffer = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    const formData = getValues();
    await onSubmit(formData, true);
  };

  const handleCancel = () => {
    reset();
    dispatch(clearOffer());
    if (isNew) {
      navigate("/service-board");
    } else {
      navigate("/service-offers");
    }
  };

  const handleStepClick = (step: number) => {
    if (step < activeStepIndex) {
      setActiveStepIndex(step);
    }
  };

  const handleReplaceOffer = async () => {
    if (!existingOffer?.id || !serviceRequestId) return;
    try {
      await offersApi.withdrawOffer(existingOffer.id);
      // Reload the page to start fresh -- the offer is now withdrawn
      setExistingOffer(null);
      setShowDuplicateDialog(false);
      // Navigate to same URL to reset the form
      navigate(`/service-offers/new/${serviceRequestId}`, { replace: true });
      window.location.reload();
    } catch (err) {
      console.error("Failed to withdraw existing offer:", err);
    }
  };

  const handleViewExistingOffer = () => {
    if (!existingOffer?.id) return;
    navigate(`/service-offers/${existingOffer.id}`);
  };

  const handleDepartureChange = (loc: DepartureLocation) => {
    departureChangedRef.current = true;
    setDepartureLocation(loc);
  };

  // Load service request for new offers
  useEffect(() => {
    const loadServiceRequest = async () => {
      if (serviceRequestId && !serviceRequest) {
        try {
          const sr = await publicServiceRequestsApi.getServiceRequestAsync(serviceRequestId);
          setFullServiceRequest(sr);
          setServiceRequest({
            id: sr.id,
            internalNumber: sr.internalNumber,
            issue: sr.issue,
            machine: {
              id: sr.machine.id,
              name: sr.machine.name,
              brandName: sr.machine.brandName,
              imageUrl: sr.machine.imageUrl,
              catalogImageUrl: sr.machine.catalogImageUrl,
            },
          });

          // Fetch customer location for directions calculation
          if (sr.customer?.id) {
            try {
              const custData = await publicCustomersApi.getCustomer(sr.customer.id);
              if (custData?.address?.latitude && custData?.address?.longitude) {
                setCustomerLocation({
                  lat: parseFloat(String(custData.address.latitude)),
                  lng: parseFloat(String(custData.address.longitude)),
                });
              }
            } catch {
              // Non-critical — directions just won't auto-calculate
            }
          }
        } catch (err) {
          console.error("Failed to load service request:", err);
        }
      }
    };

    loadServiceRequest();
  }, [serviceRequestId]);

  // Fetch provider settings (for location + pricing defaults already fetched by hook)
  useEffect(() => {
    const fetchProviderSettings = async () => {
      try {
        const settings = await serviceProviderPricingApi.getSettings();
        setProviderSettings(settings);
      } catch {
        // Non-critical
      }
    };
    fetchProviderSettings();
  }, []);

  // Check for existing offer when creating a new offer (proactive duplicate detection)
  useEffect(() => {
    if (!isNew || !serviceRequestId) return;

    offersApi
      .getExistingOfferForSR(serviceRequestId)
      .then((found) => {
        if (found) {
          setExistingOffer({
            id: found.id,
            statusCode: found.statusCode,
            laborCost: found.laborCost || 0,
            totalEstimatedCost: found.totalEstimatedCost || 0,
            createdAt: found.createdDate,
            submittedAt: found.sentDate,
            serviceApproach: found.serviceApproach,
          });
          setShowDuplicateDialog(true);
        }
      })
      .catch(() => {
        // Silently fail -- worst case the SP fills out the form and gets the backend OFFER_DUPLICATE error
      });
  }, [isNew, serviceRequestId]);

  // Load existing offer for editing
  useEffect(() => {
    if (id) {
      dispatch(getOffer(id));
    }
  }, [dispatch, id]);

  // Populate form when offer is loaded
  useEffect(() => {
    if (!offer) {
      // For new offers, form is already initialized via useForm({ defaultValues })
      // and pricing defaults are applied by the hook — don't reset or we'd wipe them.
      // For edit->new transitions the component remounts, so no stale data.
      setFiles([]);
      return;
    }

    if (offer.serviceRequest) {
      setServiceRequest(offer.serviceRequest);
    }

    let parsedAvailableDates: string[] = [];
    if (offer.availableDates) {
      try {
        parsedAvailableDates = JSON.parse(offer.availableDates);
      } catch {
        parsedAvailableDates = [];
      }
    }

    const offerFormData: OfferFormData = {
      laborCost: offer.laborCost,
      laborHours: offer.laborHours,
      hourlyRate: offer.hourlyRate,
      partsCost: offer.partsCost,
      materialDescription: offer.materialDescription || "",
      travelCost: offer.travelCost,
      travelDistanceKm: offer.travelDistanceKm,
      travelRatePerKm: offer.travelRatePerKm,
      travelTimeHours: offer.travelTimeHours,
      travelRatePerHour: offer.travelRatePerHour,
      additionalCosts: offer.additionalCosts,
      additionalCostsNote: offer.additionalCostsDescription || "",
      laborSliderAdjustment: 0,
      materialSliderAdjustment: 0,
      travelDistanceSliderAdjustment: 0,
      travelTimeSliderAdjustment: 0,
      additionalSliderAdjustment: 0,
      earliestStartDate: offer.earliestStartDate ? moment(offer.earliestStartDate) : undefined,
      immediateStart: offer.immediateStart || false,
      availableDates: parsedAvailableDates,
      estimatedDuration: offer.estimatedDuration || "",
      completionTimeCode: offer.completionTimeCode,
      customCompletionTime: offer.customCompletionTime || "",
      serviceApproach: offer.serviceApproach || "",
      specialEquipmentRequired: offer.specialEquipmentRequired || "",
      warrantyPeriodCode: offer.warrantyPeriodCode,
      customWarrantyPeriod: offer.customWarrantyPeriod || "",
      paymentTermsCode: offer.paymentTermsCode,
      customPaymentTerms: offer.customPaymentTerms || "",
      validityPeriodCode: offer.validityPeriodCode,
      customValidityPeriod: offer.customValidityPeriod || "",
      messageToCustomer: offer.messageToCustomer || "",
      includeDiagnostics: offer.includeDiagnostics,
      includeLaborForRepair: offer.includeLaborForRepair,
      includePartsAndMaterials: offer.includePartsAndMaterials,
      includeTestingAndCalibration: offer.includeTestingAndCalibration,
      includeWorkAreaCleanup: offer.includeWorkAreaCleanup,
      includeDetailedServiceReport: offer.includeDetailedServiceReport,
      confirmedBindingOffer: offer.confirmedBindingOffer,
      confirmedTermsOfService: offer.confirmedTermsOfService,
      confirmedAuthorityToOffer: offer.confirmedAuthorityToOffer,
      notifyOnViewAcceptReject: offer.notifyOnViewAcceptReject,
      notifyTeamMembers: offer.notifyTeamMembers,
    };

    reset(offerFormData);
    setFiles(offer.files || []);

    // For editing, start at pricing step (step 0)
    if (!isNew) {
      setActiveStepIndex(0);
    }
  }, [offer, reset, emptyFormData, isNew]);

  // Fetch provider's own deadline info for warning display
  useEffect(() => {
    const fetchDeadlineInfo = async () => {
      try {
        const info = await serviceProviderPricingApi.getDeadlineInfo();
        setDeadlineInfo(info);
      } catch (err) {
        // Silently fail - deadline warning is non-critical
        console.warn("Failed to fetch deadline info:", err);
      }
    };
    fetchDeadlineInfo();
  }, []);

  // Signal presence when entering/leaving the offer wizard
  useEffect(() => {
    if (!serviceRequestId) return;

    const connection = getSharedConnection();
    if (connection && connection.state === HubConnectionState.Connected) {
      connection.invoke("JoinOfferCreation", serviceRequestId).catch(console.error);
    }

    return () => {
      const conn = getSharedConnection();
      if (conn && conn.state === HubConnectionState.Connected) {
        conn.invoke("LeaveOfferCreation", serviceRequestId).catch(() => {});
      }
    };
  }, [serviceRequestId]);

  // Backup beforeunload handler (OnDisconnectedAsync is the real safety net)
  useEffect(() => {
    if (!serviceRequestId) return;

    const handleBeforeUnload = () => {
      const conn = getSharedConnection();
      if (conn && conn.state === HubConnectionState.Connected) {
        conn.invoke("LeaveOfferCreation", serviceRequestId).catch(() => {});
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [serviceRequestId]);

  // Listen for DataRefresh on serviceBoard scope to detect RFS status changes mid-wizard
  useDataRefresh("serviceBoard", () => {
    if (serviceRequestId && !rfsUnavailable) {
      publicServiceRequestsApi
        .getServiceRequestAsync(serviceRequestId)
        .then((sr: PublicServiceRequest) => {
          // statusCode 2 = OpenToOffers, 3 = Negotiating
          if (sr && sr.statusCode !== 2 && sr.statusCode !== 3) {
            setRfsUnavailable(true);
          }
        })
        .catch(() => {});
    }
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearOffer());
    };
  }, []);

  // Determine if we should show a deadline warning
  const deadlineWarning = useMemo(() => {
    if (!deadlineInfo || !deadlineInfo.isDeadlineEnabled) return null;

    if (deadlineInfo.isCurrentlyPastDeadline) {
      return {
        severity: "error" as const,
        message: translate("serviceOffers:deadlinePassedWarning"),
      };
    }

    // Parse timeUntilDeadline (TimeSpan format: "HH:mm:ss" or "d.HH:mm:ss")
    if (deadlineInfo.timeUntilDeadline) {
      const parts = deadlineInfo.timeUntilDeadline.split(":");
      if (parts.length >= 2) {
        const hourPart = parts[0].includes(".") ? parts[0].split(".")[1] : parts[0];
        const hours = parseInt(hourPart, 10);
        const minutes = parseInt(parts[1], 10);
        const totalMinutes = hours * 60 + minutes;

        if (totalMinutes <= 60) {
          // Format the deadline time for display
          const deadlineTimeStr = deadlineInfo.deadlineTime
            ? (() => {
                const tp = deadlineInfo.deadlineTime.split(":");
                return tp.length >= 2 ? `${tp[0].padStart(2, "0")}:${tp[1].padStart(2, "0")}` : deadlineInfo.deadlineTime;
              })()
            : "";

          return {
            severity: "warning" as const,
            message: translate("serviceOffers:deadlineApproachingWarning", {
              time: deadlineTimeStr,
            }),
          };
        }
      }
    }
    return null;
  }, [deadlineInfo, translate]);

  const isOfferLoading = loading && id;
  if (isOfferLoading) {
    return <Loader />;
  }

  return (
    <FormProvider {...formMethods}>
      <ActionToast
        open={!!error}
        variant="error"
        message={translate(NotificationMessage.error(error).message)}
        onClose={() => dispatch(clearError())}
        duration={6000}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
          background: dt.pageBg,
          backgroundImage: `radial-gradient(circle, ${dt.pageDotColor} 1px, transparent 1px)`,
          backgroundSize: "20px 20px",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "#FFFFFF",
            borderBottom: "1px solid #E5E7EB",
            px: isMobile ? 2 : 4,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ fontFamily: dt.font, fontSize: "18px", fontWeight: 700, color: dt.textPrimary }}>
              {isNew ? translate("createNewOffer") : `${translate("editOffer")} ${offerNumber || ""}`}
            </Box>
            {activeStepIndex === 0 && (
              <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textSecondary, mt: 0.25 }}>
                {translate("pricingHelperText")}
              </Box>
            )}
          </Box>
          <WizardStepper
            steps={stepLabels}
            activeStep={activeStepIndex}
            onStepClick={handleStepClick}
          />
        </Box>

        {/* Content */}
        <Box
          component="form"
          id="editOffer"
          onSubmit={handleSubmit(async (data) => await onSubmit(data, false))}
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            px: isMobile ? 2 : 4,
            py: 3,
            pb: 12,
            overflowX: "hidden",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: activeStepIndex === 0 ? "1200px" : "800px" }}>
            {/* RFS unavailable banner — shown when RFS closes while SP is filling the form */}
            <RfsUnavailableBanner visible={rfsUnavailable} />

            {/* Deadline warning banner */}
            {deadlineWarning && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  padding: "12px 16px",
                  borderRadius: "10px",
                  mb: 2,
                  backgroundColor: deadlineWarning.severity === "error" ? "#FEECEC" : "#FFF8E1",
                  border: `1px solid ${deadlineWarning.severity === "error" ? "#F23E3E30" : "#FFB30030"}`,
                }}
              >
                <WarningAmberIcon
                  sx={{
                    fontSize: "20px",
                    color: deadlineWarning.severity === "error" ? "#F23E3E" : "#F39C12",
                    flexShrink: 0,
                  }}
                />
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: deadlineWarning.severity === "error" ? "#C0392B" : "#7D6608",
                    lineHeight: 1.5,
                  }}
                >
                  {deadlineWarning.message}
                </Box>
              </Box>
            )}

            {/* RFS Summary — shown on EVERY step */}
            <OfferRfsSummary
              serviceRequest={serviceRequest}
              fullServiceRequest={fullServiceRequest}
            />

            {activeStepIndex === 0 && (
              <>
                {isNew && (
                  <Box sx={{ mb: 2 }}>
                    <PricingDefaultsBanner
                      hasDefaults={hasDefaults}
                      loading={pricingLoading}
                    />
                  </Box>
                )}
                <StepPricing
                  serviceRequest={serviceRequest}
                  fullServiceRequest={fullServiceRequest}
                  fieldsFromDefaults={fieldsFromDefaults}
                  onFieldChange={clearDefaultIndicator}
                  providerHasLocation={providerHasLocation}
                  travelInfoSource={travelInfoSource}
                  travelCalculating={directionsLoading}
                  onChangeDeparture={() => setShowDepartureModal(true)}
                />
              </>
            )}

            {activeStepIndex === 1 && <StepScheduling fullServiceRequest={fullServiceRequest} />}

            {activeStepIndex === 2 && (
              <StepServiceNotes
                files={files}
                onFilesChange={setFiles}
                fieldsFromDefaults={fieldsFromDefaults}
                onFieldChange={clearDefaultIndicator}
              />
            )}

            {activeStepIndex === 3 && (
              <StepReviewSend
                offer={previewOffer}
                serviceRequest={serviceRequest}
                fullServiceRequest={fullServiceRequest}
              />
            )}
          </Box>
        </Box>

        {/* Bottom navigation bar */}
        <WizardNavigation
          activeStep={activeStepIndex}
          totalSteps={STEP_COUNT}
          isSubmitting={isSubmitting || isReadOnly || rfsUnavailable}
          onNext={handleNext}
          onBack={handleBack}
          onSaveDraft={isReadOnly ? () => {} : handleSaveDraft}
          onSubmit={isReadOnly || rfsUnavailable ? () => {} : handleSubmitOffer}
          onCancel={handleCancel}
        />
      </Box>

      {/* Departure Point Modal */}
      <DeparturePointModal
        open={showDepartureModal}
        onClose={() => setShowDepartureModal(false)}
        currentDeparture={departureLocation}
        companyAddress={companyAddress}
        onDepartureChange={handleDepartureChange}
      />

      {/* Request No Longer Accepting Dialog */}
      <RequestNoLongerAcceptingDialog
        open={showClosedRfsDialog}
        onClose={() => setShowClosedRfsDialog(false)}
        showSaveAsDraft={!isNew && !!id}
        onSaveAsDraft={handleSaveDraft}
      />

      {/* Duplicate Offer Dialog */}
      <DuplicateOfferDialog
        open={showDuplicateDialog}
        onClose={() => {
          setShowDuplicateDialog(false);
          navigate("/service-board");
        }}
        existingOffer={existingOffer || { id: "", statusCode: 1, laborCost: 0, totalEstimatedCost: 0, createdAt: "" }}
        onReplaceOffer={handleReplaceOffer}
        onViewExistingOffer={handleViewExistingOffer}
      />

      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={handleSuccessClose}
        PaperProps={{
          sx: {
            borderRadius: dt.radius,
            p: 3,
            maxWidth: 440,
            textAlign: "center",
          },
        }}
      >
        <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, p: 0 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: dt.greenGlow,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 36, color: dt.green }} />
          </Box>
          <Box sx={{ fontFamily: dt.font, fontSize: "20px", fontWeight: 700, color: dt.textPrimary }}>
            {translate("offerSentTitle")}
          </Box>
          <Box sx={{ fontFamily: dt.font, fontSize: "14px", color: dt.textSecondary, lineHeight: 1.6 }}>
            {translate("offerSentMessage", { companyName: fullServiceRequest?.customer?.name || serviceRequest?.machine?.name || "" })}
          </Box>
          <Button
            variant="contained"
            onClick={handleSuccessClose}
            sx={{
              mt: 1,
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "14px",
              textTransform: "none",
              borderRadius: "8px",
              px: 4,
              py: 1,
              backgroundColor: dt.blue,
              "&:hover": { backgroundColor: "var(--accent-hover, #2980B9)" },
            }}
          >
            {translate("viewMyOffers")}
          </Button>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};

export default EditOfferPage;
